// O9 · Do it now (QO9_DoNow): working together like W, without the X; the timer starts at 0:00. Stuck makes the
// card smaller (fh.smaller first, then "Even smaller?" asks the server). Done -> O10 (lights day 0, +10 points).
// Admin funnel: o9_start when it opens, o9_done {minutes} (one decimal) on Done.
import { useEffect, useRef, useState } from 'preact/hooks';
import type { CharId } from '@shared/types';
import { C } from '@shared/content/characters';
import { LINES } from '@shared/content/lines';
import { Screen, XScene, XFace, ProgressBar, PrimaryButton, SecondaryButton } from '../../components';
import { api } from '../../api/client';
import { errorText, toast } from '../../store';
import { go } from '../../router';
import { haptic, keepAwake } from '../../native';
import { SMALLER_FINE, firstNote } from '../../content/onboarding';
import { say } from '../../lib/say';
import { minutesOf } from '../../lib/analytics';
import { activeHelp, answers, obStep, progress, setDraft, useDraft } from './draft';
import s from './O9DoNow.module.css';

const clock = (secs: number) => `${String(Math.floor(secs / 60)).padStart(2, '0')}:${String(secs % 60).padStart(2, '0')}`;
export const workedLabel = (secs: number) => (secs < 60 ? `${Math.max(1, secs)} sec` : `${Math.round(secs / 60)} min`);

export function O9DoNow() {
  const d = useDraft((x) => x);
  const ch: CharId = d.ch || 'margo';
  const c = C[ch];
  // The help they started: after "Not quite" in O8 that is the newest one.
  const fh = activeHelp(d) || d.fh;
  const thing = fh?.thing || d.said || null;
  const base = d.workCard || fh?.card || say(LINES[ch].firstCard, { n: c.name, task: thing });
  const [smaller, setSmaller] = useState<string | null>(d.evenSmaller);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const t0 = useRef(Date.now());
  const [secs, setSecs] = useState(0);
  const tm = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    obStep('o9_start', {});
    void keepAwake(true);
    const id = setInterval(() => setSecs(Math.floor((Date.now() - t0.current) / 1000)), 1000);
    return () => { clearInterval(id); if (tm.current) clearTimeout(tm.current); void keepAwake(false); };
  }, []);

  const title = smaller || base;

  const stuck = async () => {
    if (busy) return;
    // First Stuck: the server's smaller version of the main card is already here.
    if (!smaller && fh && base === fh.card && fh.smaller) { setSmaller(fh.smaller); setDraft({ evenSmaller: fh.smaller }); return; }
    setBusy(true);
    try {
      const r = await api.firstHelp({ invite: d.invite, ch, ob: answers(), smaller: true, thing: fh?.thing || d.said || '', card: title });
      const next = (r.card && r.card !== title ? r.card : r.smaller) || title;
      setSmaller(next); setDraft({ evenSmaller: next });
    } catch (e) { toast(errorText(e)); } finally { setBusy(false); }
  };

  const finish = () => {
    if (done) return;
    setDone(true);
    haptic('success');
    const worked = Math.max(1, Math.floor((Date.now() - t0.current) / 1000));
    obStep('o9_done', { minutes: minutesOf(worked) });
    const prev = d.first;
    setDraft({
      first: {
        card: title, done: true, doneAt: new Date().toISOString(), minutes: Math.max(1, Math.round(worked / 60)),
        note: prev?.note || firstNote(thing || title), time: workedLabel(worked), later: prev?.later || null,
      },
    });
    tm.current = setTimeout(() => go('O10'), 450);
  };

  return (
    <Screen theme={ch} id="O9">
      <XScene ch={ch} stage={1} mode="clear" />
      <div class={s.topShade} aria-hidden="true" />
      <div class={s.lowShade} aria-hidden="true" />
      <div class={s.top}>
        <ProgressBar pct={progress('O9', done)} overScene style={{ flex: 'none', width: '100%' }} />
        <div class={s.pill}>
          <XFace ch={ch} e={3} s={28} ring="var(--acc)" alt="" />
          <span>{`${c.name} is working too`}</span>
        </div>
      </div>
      <div class={s.spacer} />
      <div class={s.subtitle} aria-live="polite">
        <p class={s.who}>{c.name}</p>
        <p class={s.line}>{smaller ? SMALLER_FINE : say(LINES[ch].doNow, { n: c.name, task: thing })}</p>
      </div>
      <div class={s.bar}>
        <div class={s.barTop}>
          <div class={s.now}>
            <span class={s.nowLabel}>YOUR FIRST CARD</span>
            <span class={s.nowTitle}>{title}</span>
          </div>
          <span class={s.clock} role="timer" aria-label={`Time: ${clock(secs)}`}>{clock(secs)}</span>
        </div>
        <div class={s.btns}>
          <PrimaryButton h={50} icon="check" grow={1.35} onClick={finish} data-cta="done">Done</PrimaryButton>
          <SecondaryButton h={50} grow={1} onClick={() => void stuck()} busy={busy}>{smaller ? 'Even smaller?' : 'Stuck'}</SecondaryButton>
        </div>
      </div>
    </Screen>
  );
}
